'use client';
// FormNumberInput Component with React Hook Form
import { useCallback } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { InputAdornment, TextField, TextFieldProps } from '@mui/material';

interface FormNumberInputProps<T extends FieldValues> extends Omit<
  TextFieldProps,
  'name' | 'type'
> {
  control: Control<T>;
  name: FieldPath<T>;
  min?: number;
  max?: number;
  prefix?: string;
  suffix?: string;
}

export const FormNumberInput = <T extends FieldValues>({
  control,
  name,
  min,
  max,
  prefix,
  suffix,
  ...props
}: FormNumberInputProps<T>) => {
  const handleChange = useCallback(
    (onChange: (value: number | null) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === '') {
        onChange(null as never);
        return;
      }
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        onChange(numValue as never);
      }
    },
    []
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...props}
          type="number"
          value={field.value ?? ''}
          onChange={handleChange(field.onChange)}
          error={!!error}
          helperText={error?.message || props.helperText}
          inputProps={{
            min,
            max,
            ...props.inputProps,
          }}
          InputProps={{
            startAdornment: prefix ? (
              <InputAdornment position="start">{prefix}</InputAdornment>
            ) : undefined,
            endAdornment: suffix ? (
              <InputAdornment position="end">{suffix}</InputAdornment>
            ) : undefined,
            ...props.InputProps,
          }}
        />
      )}
    />
  );
};

export default FormNumberInput;
